import { forwardRef, useEffect, useRef, useState } from 'react';
import type { ForwardedRef } from 'react';
import { Calendar, Check, X, ChevronDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DemoSchedulerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * What the visitor clicked. "Start free trial" is the landing page's
   * primary CTA and has to capture the lead the same way the demo CTA
   * does — sending them straight to the App Store lost the contact
   * details entirely. Both intents post to the same `demo-request`
   * function; the intent is stamped on the message so the team can
   * tell a trial signup from a demo request in the inbox.
   */
  intent?: 'demo' | 'trial';
}

const INTENT_COPY = {
  demo: {
    title: 'Book a Demo',
    blurb: "See Garu in action. Fill in the details and we'll schedule a personalized walkthrough.",
    submit: 'Request Demo',
    thanks: "We've received your request and will be in touch within 24 hours.",
  },
  trial: {
    title: 'Start your free trial',
    blurb: 'Fill in the details and we\u2019ll set your store up with a 14-day free trial.',
    submit: 'Start free trial',
    thanks: "We've received your details and will set your trial up within 24 hours.",
  },
} as const;

interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  product: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  product: '',
  message: '',
};

// Mirrors the Lumoo modal: a short, scannable set of product
// surfaces a merchant can pick from. Kept tight so the dropdown
// reads as a qualifier, not a feature menu — the sales team uses
// it to prep the demo, not to scope a roadmap.
const PRODUCT_OPTIONS: { value: string; label: string }[] = [
  { value: 'try-on', label: 'Garu Try-On (Shopify)' },
  { value: 'size-guide', label: 'Garu Size Guide' },
  { value: 'studio', label: 'Garu Studio · content generation' },
  { value: 'api', label: 'API / headless integration' },
  { value: 'enterprise', label: 'Enterprise / multi-store' },
  { value: 'not-sure', label: "Not sure yet — show me everything" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function DemoScheduler({ isOpen, onClose, intent = 'demo' }: DemoSchedulerProps) {
  const copy = INTENT_COPY[intent];
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = setTimeout(() => firstInputRef.current?.focus(), 50);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setError(null);
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (error) setError(null);
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = form.name.trim();
    const company = form.company.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const product = form.product.trim();
    const message = form.message.trim();

    if (!name || !email || !company || !product) {
      setError('Please fill in your name, company, email, and product of interest.');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setError(null);

    // The Supabase function (demo-request) only persists name/email/company/
    // message officially, but the message body is what lands in the team's
    // inbox. We compose the message so phone + product-of-interest show up
    // in the notification email without backend changes.
    const productLabel = PRODUCT_OPTIONS.find((o) => o.value === product)?.label ?? product;
    const composedMessage = [
      intent === 'trial' ? '⚡ FREE TRIAL REQUEST (clicked "Start free trial")' : null,
      `Product of interest: ${productLabel}`,
      phone ? `Phone: ${phone}` : null,
      message ? `Notes:\n${message}` : null,
    ]
      .filter(Boolean)
      .join('\n\n');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('demo-request', {
        body: {
          name,
          email,
          company,
          message: composedMessage,
        },
      });

      if (fnError) throw fnError;
      if (data && (data as { success?: boolean }).success === false) {
        throw new Error((data as { error?: string }).error || 'Something went wrong');
      }

      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setError(`Sorry, we couldn't send your request: ${msg}. Please try again or email hello@garu.ai directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
    >
      <div
        className="absolute inset-0 bg-neutral-900/55 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-[0_24px_60px_-12px_rgba(0,0,0,0.25)] w-full max-w-[480px] max-h-[92vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 text-neutral-400 hover:text-neutral-900 rounded-full hover:bg-neutral-100 transition-colors z-10"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>

        <div className="px-7 pt-8 pb-7 sm:px-8 sm:pt-10 sm:pb-8">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-12 h-12 rounded-full bg-neutral-900 text-white flex items-center justify-center mb-5">
                <Check className="w-5 h-5" strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-medium tracking-[-0.02em] text-neutral-900 mb-2">
                Thank you.
              </h3>
              <p className="text-[14px] text-neutral-500 leading-relaxed max-w-[320px] mb-6">
{copy.thanks} A confirmation has been sent to <span className="text-neutral-900">{form.email}</span>.
              </p>
              <button
                onClick={onClose}
                className="garu-btn-cta inline-flex items-center justify-center !normal-case !tracking-normal text-[13px] font-medium px-6 py-2.5"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2
                  id="demo-modal-title"
                  className="text-[1.5rem] leading-[1.15] tracking-[-0.02em] font-semibold text-neutral-900 mb-2"
                >
                  {copy.title}
                </h2>
                <p className="text-[14px] text-neutral-500 leading-relaxed">
                  {copy.blurb}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
                <PlainField
                  ref={firstInputRef}
                  id="demo-name"
                  placeholder="Name *"
                  value={form.name}
                  onChange={handleChange('name')}
                  autoComplete="name"
                  required
                  disabled={submitting}
                />
                <PlainField
                  id="demo-company"
                  placeholder="Company *"
                  value={form.company}
                  onChange={handleChange('company')}
                  autoComplete="organization"
                  required
                  disabled={submitting}
                />
                <PlainField
                  id="demo-email"
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={handleChange('email')}
                  autoComplete="email"
                  required
                  disabled={submitting}
                />
                <PlainField
                  id="demo-phone"
                  type="tel"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  autoComplete="tel"
                  disabled={submitting}
                />

                <PlainSelect
                  id="demo-product"
                  value={form.product}
                  onChange={handleChange('product')}
                  disabled={submitting}
                  required
                >
                  <option value="" disabled>
                    Product of interest *
                  </option>
                  {PRODUCT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </PlainSelect>

                <PlainTextArea
                  id="demo-message"
                  placeholder="Anything specific you'd like to see?"
                  value={form.message}
                  onChange={handleChange('message')}
                  rows={3}
                  disabled={submitting}
                />

                {error && (
                  <p
                    role="alert"
                    className="text-[12.5px] text-red-600 leading-relaxed -mt-1"
                  >
                    {error}
                  </p>
                )}

                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="garu-btn-cta inline-flex items-center justify-center gap-2 !normal-case !tracking-normal text-[13px] font-medium min-h-[2.75rem] px-5 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:translate-y-0"
                  >
                    {submitting ? (
                      <>
                        <Spinner />
                        Sending…
                      </>
                    ) : (
                      <>
                        {copy.submit}
                        <Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Spinner = () => (
  <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

interface PlainFieldProps {
  id: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
}

// Lumoo-style placeholder-only input — single thin hairline border,
// no floating label. The placeholder carries the asterisk so the
// requirement is visible without adding a separate label row above.
const PlainField = forwardRef(function PlainField(
  {
    id,
    placeholder,
    value,
    onChange,
    type = 'text',
    required,
    disabled,
    autoComplete,
  }: PlainFieldProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  return (
    <input
      ref={ref}
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      autoComplete={autoComplete}
      placeholder={placeholder}
      className="h-12 px-3.5 rounded-lg border border-neutral-200 bg-white text-[14.5px] text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-900 transition-colors disabled:bg-neutral-50 disabled:text-neutral-500"
    />
  );
});

interface PlainSelectProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  required?: boolean;
  children: React.ReactNode;
}

const PlainSelect = ({ id, value, onChange, disabled, required, children }: PlainSelectProps) => (
  <div className="relative">
    <select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required}
      className={`h-12 w-full appearance-none pl-3.5 pr-10 rounded-lg border border-neutral-200 bg-white text-[14.5px] focus:outline-none focus:border-neutral-900 transition-colors disabled:bg-neutral-50 disabled:text-neutral-500 ${
        value ? 'text-neutral-900' : 'text-neutral-500'
      }`}
    >
      {children}
    </select>
    <ChevronDown
      className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
      strokeWidth={1.75}
    />
  </div>
);

interface PlainTextAreaProps {
  id: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  disabled?: boolean;
}

const PlainTextArea = ({ id, placeholder, value, onChange, rows = 3, disabled }: PlainTextAreaProps) => (
  <textarea
    id={id}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    disabled={disabled}
    className="px-3.5 py-3 rounded-lg border border-neutral-200 bg-white text-[14.5px] text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-900 transition-colors disabled:bg-neutral-50 disabled:text-neutral-500 resize-none leading-relaxed"
  />
);

export function DemoCTA({
  onScheduleDemo,
  variant = 'default',
}: {
  onScheduleDemo: () => void;
  variant?: 'default' | 'dark' | 'outline';
}) {
  const variantClasses = {
    default: 'garu-btn-cta !normal-case !tracking-normal',
    dark: 'garu-btn-cta !normal-case !tracking-normal',
    outline: 'garu-btn-secondary !normal-case !tracking-normal',
  };

  return (
    <button
      type="button"
      onClick={onScheduleDemo}
      className={`inline-flex items-center justify-center gap-2 ${variantClasses[variant]}`}
    >
      <Calendar className="w-4 h-4" strokeWidth={1.75} />
      Book a demo
    </button>
  );
}

export default DemoScheduler;
